import { useEffect, useRef, type RefObject } from 'react';

// Swipe-to-dismiss for a bottom sheet (design system 03-components §4.1: sheets "SHOULD support swipe-to-dismiss — but
// swipe MUST NOT be the only dismissal"; Phase 1c finding G10). Below --bp-md a dialog and a menu become sheets drawn with
// a grab handle, and nothing answered a drag on it: the handle promised a gesture that did nothing. A press on a sheet's
// swipe zone — the handle, and a dialog's header — that is not on a control drags the sheet down with the finger; let
// go past a distance, or flicked, the sheet is dismissed exactly as a scrim tap dismisses it (a dialog holding unsaved
// input asks first, §4.1), and otherwise it settles back. Escape, the scrim and the close or Cancel control all remain.

/** The attribute marking where a press may start a swipe. */
export const SWIPE_ZONE_ATTRIBUTE = 'data-swipe-zone';
/** A drag this far down dismisses, or a third of the sheet's height when that is less. */
export const SWIPE_DISMISS_DISTANCE_PX = 96;
export const SWIPE_DISMISS_FRACTION = 1 / 3;
/** A shorter drag still dismisses when it is a flick: at least this far, at least this fast (pixels a millisecond). */
export const SWIPE_FLICK_MIN_PX = 24;
export const SWIPE_FLICK_SPEED = 0.5;

/** What a drag that ended `distance` pixels down, `elapsedMs` after it began, on a sheet `height` tall, does (pure). */
export function swipeOutcome({ distance, elapsedMs, height }: { distance: number; elapsedMs: number; height: number }): 'dismiss' | 'settle' {
  if (!(distance > 0)) return 'settle';
  const threshold = height > 0 ? Math.min(SWIPE_DISMISS_DISTANCE_PX, height * SWIPE_DISMISS_FRACTION) : SWIPE_DISMISS_DISTANCE_PX;
  if (distance >= threshold) return 'dismiss';
  const speed = distance / Math.max(elapsedMs, 1);
  return distance >= SWIPE_FLICK_MIN_PX && speed >= SWIPE_FLICK_SPEED ? 'dismiss' : 'settle';
}

// Why a press on a control never drags: the close button sits in a dialog's header, and a tap on it must stay a tap.
const CONTROL_SELECTOR = 'button, a[href], input, select, textarea, [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

/** Whether a press on `target` may start a swipe (pure over the element's ancestry). */
export function startsSwipe(target: Element | null): boolean {
  if (!target) return false;
  return target.closest(`[${SWIPE_ZONE_ATTRIBUTE}]`) !== null && target.closest(CONTROL_SELECTOR) === null;
}

/**
 * Drags the sheet `panel` down from its swipe zone while `enabled` — the sheet form only — and calls `onDismiss` when a
 * drag ends past the distance or as a flick. The sheet follows the finger by its inline transform and settles back by the
 * sheet's own transition (components.css), which reduced motion removes.
 */
export function useSwipeToDismiss(panel: RefObject<HTMLElement | null>, enabled: boolean, onDismiss: () => void): void {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    const sheet = panel.current;
    if (!enabled || !sheet) return undefined;
    let drag: { pointer: number; y: number; at: number } | null = null;

    const release = (): void => {
      drag = null;
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
    };
    const onDown = (event: PointerEvent): void => {
      if (drag || !event.isPrimary || event.button !== 0) return;
      if (!startsSwipe(event.target instanceof Element ? event.target : null)) return;
      drag = { pointer: event.pointerId, y: event.clientY, at: event.timeStamp };
      sheet.classList.add('is-dragging');
      try {
        sheet.setPointerCapture(event.pointerId);
      } catch {
        // Without capture the drag still ends on the pointer's own up or cancel.
      }
    };
    const onMove = (event: PointerEvent): void => {
      if (!drag || event.pointerId !== drag.pointer) return;
      const distance = Math.max(0, event.clientY - drag.y);
      sheet.style.transform = distance > 0 ? `translateY(${distance}px)` : '';
    };
    const onUp = (event: PointerEvent): void => {
      if (!drag || event.pointerId !== drag.pointer) return;
      const outcome = swipeOutcome({ distance: event.clientY - drag.y, elapsedMs: event.timeStamp - drag.at, height: sheet.getBoundingClientRect().height });
      release();
      if (outcome === 'dismiss') dismiss.current();
    };
    const onCancel = (event: PointerEvent): void => {
      if (drag && event.pointerId === drag.pointer) release();
    };

    sheet.addEventListener('pointerdown', onDown);
    sheet.addEventListener('pointermove', onMove);
    sheet.addEventListener('pointerup', onUp);
    sheet.addEventListener('pointercancel', onCancel);
    return () => {
      sheet.removeEventListener('pointerdown', onDown);
      sheet.removeEventListener('pointermove', onMove);
      sheet.removeEventListener('pointerup', onUp);
      sheet.removeEventListener('pointercancel', onCancel);
      release();
    };
  }, [panel, enabled]);
}
